<template>
  <div :class="$style.base">
    <Flex :gap=".5">
      <BaseText inline>{{ $t('customers.segments.description') }}</BaseText>
      <BaseText
        :href="helpCenterArticleUrl(12381564)"
        inline
      >
        {{ $t('global.learn_more') }}
      </BaseText>
    </Flex>
    <div :class="$style.inner">
      <BaseSwitch
        v-model="selectedKey"
        :options="segmentKeys.map((value) => (
          {
            label: `${$t(`customers.segments.segments.${value}.heading`)} ${segments.length ? `(${segments.find(({key}) => key === value)?.customersCount})` : ''}`,
            value
          }
        ))"
        v-test="'customer-segments-switch'"
      />
      <div :class="$style.segmentHeader">
        <Flex direction="column">
          <BaseHeading v-test="'customer-segment-heading'">
            {{ `${$t(`customers.segments.segments.${selectedKey}.heading`)} ${selectedSegment ? `(${selectedSegmentCount})` : ''}` }}
          </BaseHeading>
          <BaseText>
            {{ $t(`customers.segments.segments.${selectedKey}.description`) }}
          </BaseText>
        </Flex>
        <BaseButton
          v-show="hasCustomerCount"
          @click="showMessageTypeModal = true"
          v-test="'btn-send-message'"
        >
          {{ $t('customers.send_message') }}
        </BaseButton>
      </div>
      <CustomersList
        v-model="hasCustomerCount"
        :segmentKey="selectedKey"
      />
    </div>
    <MessageType
      v-if="showMessageTypeModal"
      :segmentKey="selectedKey"
      @select="(type) => {
      showSendMessageModal = true;
      selectedMessageType = type;
      showMessageTypeModal = false;
    }"
      @close="showMessageTypeModal = false"
    />
    <SendMessage
      v-if="showSendMessageModal"
      :segmentKey="selectedKey"
      :selectedMessageType="selectedMessageType"
      @close="showSendMessageModal = false"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'CustomersSegments',
};
</script>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { useRoute, useRouter } from 'vue-router';
import { dashToUnderscore, underscoreToDash } from '@/helpers/formatting';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import type { CustomersSegment } from '@/types';
import MessageType from '../all/MessageType.vue';
import SendMessage from '../SendMessage.vue';
import CustomersList from './CustomersList.vue';
import { GET_CUSTOMERS_SEGMENTS } from './graphql';

const segmentKeys = ['first_visit_follow_ups', 'lapsing_regulars'];
const selectedKey = ref(segmentKeys[0]);

const { result } = useQuery<{ customersSegments: CustomersSegment[] }>(
  GET_CUSTOMERS_SEGMENTS,
);
const hasCustomerCount = ref(false);

const segments = computed(() => result.value?.customersSegments || []);
const selectedSegment = computed(() =>
  segments.value.find(({ key }) => key === selectedKey.value),
);
const selectedSegmentCount = computed(
  () => selectedSegment.value?.customersCount || 0,
);
provide('totalCount', selectedSegmentCount);

const showMessageTypeModal = ref(false);
const showSendMessageModal = ref(false);
const selectedMessageType = ref('email');

const route = useRoute();
const router = useRouter();

watch(
  () => route.params.key,
  (key) => {
    if (!key) {
      return;
    }
    selectedKey.value = dashToUnderscore(key) as string;
  },
  {
    immediate: true,
  },
);

watch(
  selectedKey,
  () => {
    router.push({
      name: 'customers-segments',
      params: {
        key: underscoreToDash(selectedKey.value),
      },
    });
  },
  {
    immediate: true,
  },
);
</script>

<style
  lang="scss"
  module
>
.base, .inner {
  display: flex;
  flex-direction: column;
  gap: $spacing * 2;
}

.segmentHeader {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $spacing;
}
</style>
